import { observable, action, reaction, makeObservable } from 'mobx';
import swal from 'sweetalert';

/**
 * `alertData` is an object like:
 *
 * ```
 * {
 *   alert: { title: string, text: string, icon: string },
 *   afterRun: func,
 *   id: any
 * }
 * ```
 *
 * Call `add` with function returning `alertData` as above.
 * Provided `callback` should be run in `afterRun` function, plus any other code that should be run after user accepts popup.
 * `callback` function must be called to advance state machine.
 *
 * PlantUML sequence diagram for HelperEventStore:
 *
 * @startuml
 * Component -> HelperEventStore: add(callback => alertData(callback))
 * HelperEventStore --> HelperEventStore: reaction this.alertRunner => currentAlert = alertData
 * Client <- HelperEventStore: swal(currentAlert.alert)
 * Client -> HelperEventStore: confirm
 * HelperEventStore --> HelperEventStore: alertData.afterRun()
 * @enduml
 **/

class HelperEventStore {
  queue = [];
  currentAlert = {};

  constructor() {
    makeObservable(this, {
      queue: observable,
      currentAlert: observable,
      add: action,
      pop: action,
    });

    this.alertRunner = reaction(
      () => this.currentAlert,
      alert => {
        if (alert.alert) {
          swal({ ...this.currentAlert.alert }).then(confirm => {
            this.currentAlert.afterRun();
          });
        }
      },
    );
  }

  add(alertData) {
    if (Object.keys(this.currentAlert).length < 1) {
      this.currentAlert = alertData(this.pop.bind(this));
    } else {
      this.queue.push(alertData(this.pop.bind(this)));
    }
  }

  pop(timestamp) {
    if (this.queue.length === 0) {
      this.currentAlert = {};
    } else {
      this.currentAlert = this.queue.pop();
    }
  }
}

const HelperStoreInstance = new HelperEventStore();

export default HelperStoreInstance;
